import type { CSSProperties, ReactNode } from 'react'
import { WidgetDensityProvider } from './WidgetDensityContext'
import './DashboardGrid.css'

/** Column span at each breakpoint, out of 12. `md`/`lg` default to the previous tier (mobile-first —
 * a widget only needs to say how it gets WIDER as room opens up). */
export interface DashboardGridSpan {
  base: number
  md?: number
  lg?: number
}

export interface DashboardGridWidget {
  /** Stable key — also the React key and the `data-widget-key` used by tests. */
  key: string
  span: DashboardGridSpan
  minHeight?: number
  render: () => ReactNode
}

export interface DashboardGridSection {
  id: string
  /** Sections with no `title` render with no VISIBLE heading. If the section also has no
   * `hiddenHeadingLabel` AND wraps exactly one widget, no heading is rendered at all — that lone
   * widget's own card heading already provides screen-reader navigation, so a section-level heading
   * with the SAME text would just be a duplicate accessible name (see `hiddenHeadingLabel`'s doc and
   * the "Readiness" section below for why this matters). */
  title?: string
  /** Visually-hidden heading text used ONLY when `title` is unset and the section has more than one
   * widget (or is otherwise not eligible for the "let the lone widget's own heading carry it" case
   * above) — e.g. the untitled hero KPI row, which has 3 widgets and no natural single heading to
   * borrow. */
  hiddenHeadingLabel?: string
  description?: string
  widgets: DashboardGridWidget[]
  /** `hero` gets a tighter gap and is meant to be paired with short `minHeight` cells (compact KPI
   * tiles) — the resulting short, wide cells are what drives those tiles into `compact` density via
   * the ordinary `WidgetDensityProvider` measurement, no separate "hero mode" needed in the tiles
   * themselves. */
  variant?: 'default' | 'hero'
}

export interface DashboardGridProps {
  sections: DashboardGridSection[]
}

/**
 * The 12-col responsive dashboard grid (IB-311 Slice 1, design-system.md §5) — data-driven from
 * `sections`/`widgets`, not hardcoded JSX. Replaces `DashboardScreen`'s old flat `auto-fit` grid
 * (`.iw-dashboard-grid`, `charts/charts.css`) with real visual hierarchy: named sections, explicit
 * per-widget column span across three breakpoints, and a `WidgetDensityProvider` around every cell
 * so its widget can self-adjust its own level of detail to the room it actually got (§5's
 * "auto-density"). Reflows cleanly from wide desktop down to a single narrow column — the grid never
 * exceeds 12 columns of total span, so there is no horizontal scroll and no overlap at any width.
 */
export function DashboardGrid({ sections }: DashboardGridProps) {
  return (
    <div className="iw-dashboard-grid-v2">
      {sections.map((section) => (
        // A plain grouping `<div>`, not a `<section>` landmark: with as many as 5 groupings on one
        // page, several titled only by a single child widget's own name (e.g. "Readiness" wrapping
        // just the one "Readiness" tile), a `<section aria-label>` per group would duplicate that
        // child widget card's OWN `aria-label` landmark one level up — axe's `landmark-unique`
        // rightly flags two same-named landmarks nested inside each other. The `<h2>` heading below
        // still gives screen-reader users real, headings-based section navigation (design-system.md
        // §7 "semantic landmarks") without manufacturing a redundant region -- EXCEPT when a section
        // has exactly one widget carrying that SAME title (e.g. "Readiness"): that widget's own card
        // heading already IS the section's heading, so the registry omits `title` there (see
        // `dashboardWidgetRegistry.tsx`'s "readiness" section) and this component renders no
        // section-level heading at all rather than a second `<h2>` with identical, duplicate
        // accessible text one level up.
        <div key={section.id} className="iw-dashboard-section">
          {section.title ? (
            <div className="iw-dashboard-section__header">
              <h2 id={`${section.id}-heading`} className="iw-dashboard-section__title">
                {section.title}
              </h2>
              {section.description ? <p className="iw-dashboard-section__desc">{section.description}</p> : null}
            </div>
          ) : section.hiddenHeadingLabel ? (
            <h2 className="iw-visually-hidden">{section.hiddenHeadingLabel}</h2>
          ) : null}
          <div
            className={`iw-dashboard-section__grid${section.variant === 'hero' ? ' iw-dashboard-section__grid--hero' : ''}`}
          >
            {section.widgets.map((widget) => {
              const style: CSSProperties & Record<string, string | number> = {
                '--cell-span-base': widget.span.base,
                '--cell-span-md': widget.span.md ?? widget.span.base,
                '--cell-span-lg': widget.span.lg ?? widget.span.md ?? widget.span.base,
              }
              if (widget.minHeight) style.minHeight = widget.minHeight
              return (
                <div key={widget.key} className="iw-dashgrid-cell" data-widget-key={widget.key} style={style}>
                  <WidgetDensityProvider>{widget.render()}</WidgetDensityProvider>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
